import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", event_date: "", venue: "", message: "" };

const fields = [
  { key: "name", label: "YOUR NAME", type: "text", placeholder: "e.g. PROMOTER X" },
  { key: "email", label: "EMAIL", type: "email", placeholder: "you@warehouse.tv" },
  { key: "event_date", label: "EVENT DATE", type: "text", placeholder: "e.g. 2026-09-12" },
  { key: "venue", label: "VENUE / CITY", type: "text", placeholder: "e.g. BASEMENT 9, MIAMI" },
];

export default function Booking() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setSent(true);
      setForm(initial);
      toast.success("TRANSMISSION RECEIVED — we'll be in touch.");
    } catch (err) {
      toast.error("SIGNAL LOST — check the fields and try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 focus:border-[#ff3b1d] outline-none font-mono-x text-sm tracking-wider py-3 placeholder:text-white/25 transition-colors duration-200";

  return (
    <section id="booking" data-testid="booking-section" className="border-b border-white/15">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="px-4 sm:px-8 py-20 sm:py-28 border-b lg:border-b-0 lg:border-r border-white/15">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-mono-x text-[11px] tracking-[0.3em] text-white/40 uppercase mb-4"
            data-testid="booking-overline"
          >
            [05] — BOOKING
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-mono-x font-extrabold uppercase tracking-tighter leading-[0.9] text-4xl sm:text-6xl"
            data-testid="booking-title"
          >
            BRING THE
            <br />
            <span className="text-[#ff3b1d]">PRESSURE</span>
          </motion.h2>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-10 space-y-4"
            data-testid="booking-contact-info"
          >
            <p className="max-w-md text-sm leading-relaxed text-white/60">
              Club nights, warehouse takeovers, festival slots, private machines. Send the
              details — the colony answers fast.
            </p>
            <a
              href="tel:+17723324038"
              data-testid="booking-phone-link"
              className="inline-block font-mono-x text-lg sm:text-xl font-bold tracking-tight border border-white/20 px-6 py-4 hover:bg-white hover:text-black transition-colors duration-200"
            >
              772-332-4038
            </a>
          </motion.div>
        </div>

        <div className="px-4 sm:px-8 py-20 sm:py-28">
          {sent ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="h-full flex flex-col items-start justify-center gap-6"
              data-testid="booking-success"
            >
              <p className="font-mono-x text-[11px] tracking-[0.3em] text-[#ff3b1d] uppercase">
                // TRANSMISSION RECEIVED
              </p>
              <p className="font-mono-x text-2xl sm:text-3xl font-bold uppercase tracking-tighter leading-tight">
                The colony has your signal. Expect contact within 48 hours.
              </p>
              <button
                onClick={() => setSent(false)}
                data-testid="booking-send-another"
                className="font-mono-x text-[11px] tracking-[0.25em] border border-white/20 px-6 py-3 hover:bg-white hover:text-black transition-colors duration-200"
              >
                SEND ANOTHER
              </button>
            </motion.div>
          ) : (
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="space-y-8"
              data-testid="booking-form"
            >
              {fields.map((f) => (
                <div key={f.key}>
                  <label
                    htmlFor={`booking-${f.key}`}
                    className="block font-mono-x text-[10px] tracking-[0.3em] text-white/40 uppercase mb-1"
                  >
                    {f.label} <span className="text-[#ff3b1d]">*</span>
                  </label>
                  <input
                    id={`booking-${f.key}`}
                    data-testid={`booking-input-${f.key.replace("_", "-")}`}
                    type={f.type}
                    required
                    value={form[f.key]}
                    onChange={update(f.key)}
                    placeholder={f.placeholder}
                    className={inputCls}
                  />
                </div>
              ))}
              <div>
                <label
                  htmlFor="booking-message"
                  className="block font-mono-x text-[10px] tracking-[0.3em] text-white/40 uppercase mb-1"
                >
                  MESSAGE
                </label>
                <textarea
                  id="booking-message"
                  data-testid="booking-input-message"
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="CAPACITY, SET LENGTH, LINEUP, BUDGET..."
                  className={`${inputCls} resize-none border border-white/20 focus:border-[#ff3b1d] p-3`}
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="booking-submit-button"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 font-mono-x text-xs tracking-[0.3em] font-bold bg-white text-black px-10 py-4 hover:bg-[#ff3b1d] hover:text-black transition-colors duration-200 disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> TRANSMITTING
                  </>
                ) : (
                  "TRANSMIT REQUEST"
                )}
              </button>
            </motion.form>
          )}
        </div>
      </div>
    </section>
  );
}
